import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // ~60s

export function PaymentSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "polling", data: null, attempts: 0 });
    const pollsRef = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", data: null, error: "Missing session id" });
            return;
        }

        let cancelled = false;
        const tick = async () => {
            if (cancelled) return;
            pollsRef.current += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setState({ status: "paid", data });
                    return;
                }
                if (["failed", "expired"].includes(data.payment_status)) {
                    setState({ status: "failed", data });
                    return;
                }
                if (pollsRef.current >= MAX_POLLS) {
                    setState({ status: "timeout", data });
                    return;
                }
                setState({ status: "polling", data, attempts: pollsRef.current });
                setTimeout(tick, POLL_INTERVAL_MS);
            } catch (err) {
                if (cancelled) return;
                setState({
                    status: "error",
                    data: null,
                    error:
                        err?.response?.data?.detail ||
                        err.message ||
                        "Could not confirm payment",
                });
            }
        };
        tick();
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    const money =
        state.data?.amount != null && state.data?.currency
            ? `${state.data.currency.toUpperCase()} ${(state.data.amount / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : null;

    const goHome = () => navigate("/member", { replace: true });

    return (
        <div className="min-h-screen bg-cream flex items-center justify-center p-6">
            <div
                data-testid="payment-success-page"
                className="w-full max-w-lg rounded-3xl bg-white shadow-lift p-10 text-center"
            >
                {state.status === "polling" && (
                    <>
                        <Loader2 className="mx-auto text-gold-600 mb-6 animate-spin" size={44} />
                        <h1 className="font-display text-3xl text-navy-700 mb-2">
                            Confirming your payment…
                        </h1>
                        <p className="text-navy-500">
                            Hang tight — we're checking with Stripe.
                        </p>
                    </>
                )}
                {state.status === "paid" && (
                    <>
                        <CheckCircle2 className="mx-auto text-emerald-600 mb-6" size={54} />
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                            Payment confirmed
                        </div>
                        <h1 className="font-display text-3xl text-navy-700 mb-2">
                            Thanks for joining the flock 🐼
                        </h1>
                        {state.data?.product_name && (
                            <div className="text-navy-500 mb-1">
                                {state.data.product_name}
                            </div>
                        )}
                        {money && (
                            <div className="font-display text-2xl text-navy-700 mb-6">
                                {money}
                            </div>
                        )}
                        <p className="text-navy-500 leading-relaxed mb-8">
                            A receipt is on its way to your inbox. Your perks are now
                            unlocked in the member dashboard.
                        </p>
                        <Button
                            data-testid="payment-success-cta"
                            onClick={goHome}
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                        >
                            Go to my dashboard <ArrowRight size={16} className="ml-2" />
                        </Button>
                    </>
                )}
                {state.status === "failed" && (
                    <>
                        <XCircle className="mx-auto text-red-600 mb-6" size={54} />
                        <h1 className="font-display text-3xl text-navy-700 mb-2">
                            Payment didn't go through
                        </h1>
                        <p className="text-navy-500 mb-8">
                            Stripe reported the payment as {state.data?.payment_status}. No
                            charge has been made. You can try again with a different card.
                        </p>
                        <Link to="/pricing">
                            <Button className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6">
                                Try again
                            </Button>
                        </Link>
                    </>
                )}
                {state.status === "timeout" && (
                    <>
                        <Loader2 className="mx-auto text-gold-600 mb-6" size={44} />
                        <h1 className="font-display text-3xl text-navy-700 mb-2">
                            Still confirming…
                        </h1>
                        <p className="text-navy-500 mb-8">
                            Your payment may still be processing. We'll email your receipt
                            as soon as it's confirmed — usually within a couple of minutes.
                        </p>
                        <Button
                            onClick={goHome}
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                        >
                            Continue to dashboard
                        </Button>
                    </>
                )}
                {state.status === "error" && (
                    <>
                        <XCircle className="mx-auto text-red-600 mb-6" size={54} />
                        <h1 className="font-display text-3xl text-navy-700 mb-2">
                            Couldn't confirm your payment
                        </h1>
                        <p className="text-navy-500 mb-2">{state.error}</p>
                        <p className="text-navy-500 mb-8 text-sm">
                            If money left your account, please contact support quoting
                            session <code className="text-navy-700 font-mono">{sessionId}</code>.
                        </p>
                        <Link to="/">
                            <Button className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6">
                                Back to home
                            </Button>
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}

export function PaymentCancel() {
    return (
        <div className="min-h-screen bg-cream flex items-center justify-center p-6">
            <div
                data-testid="payment-cancel-page"
                className="w-full max-w-lg rounded-3xl bg-white shadow-lift p-10 text-center"
            >
                <XCircle className="mx-auto text-navy-500 mb-6" size={54} />
                <div className="text-xs uppercase tracking-[0.3em] text-navy-500 mb-2">
                    Checkout cancelled
                </div>
                <h1 className="font-display text-3xl text-navy-700 mb-3">
                    No charge was made
                </h1>
                <p className="text-navy-500 mb-8">
                    You cancelled the payment before finishing. Your card was not
                    charged. When you're ready to unlock the perks, we'll be here.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/pricing">
                        <Button className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6 w-full sm:w-auto">
                            Try again
                        </Button>
                    </Link>
                    <Link to="/">
                        <Button
                            variant="outline"
                            className="rounded-full border-navy-100 text-navy-700 h-11 px-6 w-full sm:w-auto"
                        >
                            Back to home
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
